/**
 * Shared source of truth for the Pulse "Sample" preview shown to hosts on
 * lower tiers (everyone who hasn't upgraded to Professional).
 *
 * Both the client preview card (PulseSamplePreview) and the server
 * sample-briefing audio endpoint derive from this single list, so the spoken
 * sample briefing can never drift from the items displayed on screen.
 *
 * The sample content is identical for every host, so the derived script is a
 * fixed string (no Gemini needed) and the synthesized MP3 is process-cached.
 */
export interface PulseSampleItem {
  headline: string;
  whyItMatters: string;
  priority: "high" | "medium" | "low";
  category: "Inbox" | "Reviews" | "Booking Requests";
}

export const PULSE_SAMPLE_ITEMS: readonly PulseSampleItem[] = [
  {
    headline: "3 member messages have been waiting over 4 hours",
    whyItMatters:
      "Fast replies keep your response rate high and protect your search ranking on PadSplit.",
    priority: "high",
    category: "Inbox",
  },
  {
    headline: "A new 3-star review came in for Maple Ave",
    whyItMatters:
      "Responding within 24 hours shows future members you take feedback seriously.",
    priority: "medium",
    category: "Reviews",
  },
  {
    headline: "New booking request for 28th Ave – Unit A",
    whyItMatters:
      "Booking requests that sit for more than a day are far more likely to fall through.",
    priority: "medium",
    category: "Booking Requests",
  },
] as const;

/**
 * Build the fixed spoken sample-briefing script from the displayed sample
 * items. Deriving it here (rather than hand-writing a separate string) keeps
 * the audio and the on-screen items in lockstep.
 */
export function buildPulseSampleScript(): string {
  const lines = PULSE_SAMPLE_ITEMS.map(
    (item) => `${item.headline}. ${item.whyItMatters}`,
  );
  return [
    "Good morning. Here's a quick sample of what Pulse watches for you.",
    ...lines,
    "Upgrade to Professional to get a briefing like this, built from your own data, every morning.",
  ].join(" ");
}
